import React, { useState } from 'react';
import { CheckCircle2Icon, CircleAlertIcon } from 'lucide-react';
import { useBilling } from '../../../contexts/BillingContext';
import { formatDateTime, fromN, toN, todayLusaka } from '../../../utils/billingMoney';
import { exportCsv } from '../../../utils/hrExport';
import { Button } from '../../../components/ui/Button';
import { SettingsPanel } from './SettingsPanel';

/** Exports, backups, migration from the old billing system, and integration health. */
export function DataSettings() {
  const b = useBilling();
  const {
    config,
    can
  } = b;
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{
    ok: boolean;
    text: string;
  } | null>(null);
  const exportAllowed = can('exportData');
  const backup = () => {
    const payload = {
      exportedAt: new Date().toISOString(),
      timezone: 'Africa/Lusaka',
      config,
      items: b.items,
      accounts: b.accounts.filter(a => a.type !== 'Patient' || b.documents.some(d => d.accountId === a.id)),
      documents: b.documents,
      directSales: b.sales,
      payments: b.payments,
      creditNotes: b.creditNotes,
      refunds: b.refunds,
      recurring: b.recurring,
      chargeClaims: b.claims,
      audit: b.audit
    };
    const url = URL.createObjectURL(new Blob([JSON.stringify(payload, null, 2)], {
      type: 'application/json'
    }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `bmed-billing-backup-${todayLusaka()}.json`;
    a.click();
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
  };
  const ledger = () => exportCsv(`Billing-transactions-${todayLusaka()}`, [{
    key: 'date',
    label: 'Date'
  }, {
    key: 'type',
    label: 'Type'
  }, {
    key: 'number',
    label: 'Number'
  }, {
    key: 'status',
    label: 'Status'
  }, {
    key: 'account',
    label: 'Account'
  }, {
    key: 'net',
    label: 'Net excl. tax',
    align: 'right'
  }, {
    key: 'tax',
    label: 'Tax',
    align: 'right'
  }, {
    key: 'total',
    label: 'Total',
    align: 'right'
  }, {
    key: 'method',
    label: 'Method'
  }, {
    key: 'staff',
    label: 'Staff'
  }], [...b.documents.filter(d => d.type === 'invoice' && d.state !== 'Draft').map(d => ({
    date: d.date,
    type: 'Invoice',
    number: d.number ?? '',
    status: d.state,
    account: d.accountName,
    net: fromN(toN(d.total) - toN(d.taxTotal)),
    tax: d.taxTotal,
    total: d.total,
    method: '',
    staff: d.issuedByName ?? d.staffName
  })), ...b.sales.map(s => ({
    date: s.date,
    type: 'Direct sale',
    number: s.number,
    status: s.state,
    account: s.accountName,
    net: fromN(toN(s.total) - toN(s.taxTotal)),
    tax: s.taxTotal,
    total: s.total,
    method: s.tenders.map(t => t.methodName).join(' + '),
    staff: s.cashierName
  })), ...b.payments.map(p => ({
    date: p.date,
    type: p.kind === 'Deposit' ? 'Deposit' : 'Invoice payment',
    number: p.receiptNumber,
    status: p.state,
    account: p.accountName,
    net: '',
    tax: '',
    total: p.amount,
    method: p.methodName,
    staff: p.receivedByName
  })), ...b.creditNotes.map(c => ({
    date: c.date,
    type: 'Credit note',
    number: c.number,
    status: c.state,
    account: c.accountName,
    net: '',
    tax: '',
    total: -c.amount,
    method: '',
    staff: c.createdByName
  })), ...b.refunds.map(r => ({
    date: r.date,
    type: 'Refund',
    number: r.number,
    status: 'Paid out',
    account: r.accountName,
    net: '',
    tax: '',
    total: -r.amount,
    method: r.methodName,
    staff: r.createdByName
  }))].sort((x, y) => x.date.localeCompare(y.date)));
  const migrate = async () => {
    if (!window.confirm('Copy invoices, receipts, payments, credit notes, quotations, items and customers from the previous billing system? The old records are left untouched.')) return;
    setBusy(true);
    const result = await b.runMigration();
    setBusy(false);
    setMsg({
      ok: result.ok,
      text: result.ok ? result.message ?? 'Done.' : result.error ?? 'Migration failed.'
    });
  };
  const unmapped = b.pendingCharges.filter(c => c.unitPrice === null).length;
  const lowStock = b.items.filter(i => i.status === 'Active' && i.kind === 'Stock' && (i.quantityOnHand ?? 0) <= (i.reorderLevel ?? 0)).length;
  const report = config.migration.report;
  const checks = [{
    ok: true,
    label: 'Database',
    detail: 'Connected to Firestore — every save is confirmed by the database before success is shown.'
  }, {
    ok: unmapped === 0,
    label: 'Hospital service mapping',
    detail: unmapped ? `${unmapped} unbilled hospital charge(s) have no matching item or price. Add the service in Items & Services (use "Also matches" for clinical names).` : 'Every unbilled consultation, test, scan and dispensed medicine has a price.'
  }, {
    ok: lowStock === 0,
    label: 'Billing inventory',
    detail: `${lowStock ? `${lowStock} stock item(s) are at or below their reorder level. ` : ''}Billing inventory is independent of Pharmacy Inventory — cash sales reduce billing stock only.`
  }, {
    ok: false,
    label: 'Email delivery',
    detail: 'Not connected — needs a transactional email provider called from a server function.'
  }, {
    ok: false,
    label: 'Card / mobile money provider',
    detail: 'Not connected — payments are recorded after they are collected; nothing is charged from Billing.'
  }, {
    ok: false,
    label: 'Server-side permission enforcement',
    detail: 'Blocked: staff sign in with the app\'s own user table, not Firebase Authentication, so Firestore security rules cannot identify the user. Moving logins to Firebase Authentication (with each user\'s role as a custom claim) and adding rules for the billing_* collections — or moving billing writes into Cloud Functions — is required before the database itself can reject unauthorised actions.'
  }];
  return <div className="space-y-4" data-id="element-5268" data-mp="munk3hl39sdp">
      <SettingsPanel title="Data & integrations" description="Exports and backups are restricted to roles with export permission. Financial records are never deleted from here." data-id="element-5269" data-mp="iu3km7d1mcoe">
        <div className="grid gap-3 sm:grid-cols-3" data-id="element-5270" data-mp="e0sbu0oo7ltu">
          <div className="rounded-md border border-slate-200 p-3" data-id="element-5271" data-mp="92z2iys2mbpc"><p className="text-sm font-semibold text-slate-900" data-id="element-5272" data-mp="16jfs92sq2col">Financial data</p><p className="mt-1 text-xs text-slate-500" data-id="element-5273" data-mp="16kqvn1i99if6">All invoices, direct sales, payments, credit notes and refunds as CSV.</p><Button size="sm" variant="outline" className="mt-2" disabled={!exportAllowed} onClick={ledger} data-id="element-5274" data-mp="11quz83hn43m4">Export CSV</Button></div>
          <div className="rounded-md border border-slate-200 p-3" data-id="element-5275" data-mp="1pokkkmfy5m36"><p className="text-sm font-semibold text-slate-900" data-id="element-5276" data-mp="1pn9kawlpmwgt">Full backup</p><p className="mt-1 text-xs text-slate-500" data-id="element-5277" data-mp="1bvl86tbgo7zu">Every billing record, settings and audit trail as a JSON file.</p><Button size="sm" variant="outline" className="mt-2" disabled={!exportAllowed} onClick={backup} data-id="element-5278" data-mp="1fwwxsyuc4t81">Download backup</Button></div>
          <div className="rounded-md border border-slate-200 p-3" data-id="element-5279" data-mp="120t9fm657f3h"><p className="text-sm font-semibold text-slate-900" data-id="element-5280" data-mp="16yl2ijiix799">Items import / export</p><p className="mt-1 text-xs text-slate-500" data-id="element-5281" data-mp="sya670yg30gp">Validated CSV import and export live on Items &amp; Services.</p></div>
        </div>
        <p className="text-xs text-slate-500" data-id="element-5282" data-mp="wzvqv8h80j1g">Restore is deliberately not offered in the app: overwriting posted financial records would break the audit trail. Restore from a backup through the Firebase console, under administrator supervision.</p>
      </SettingsPanel>

      <SettingsPanel title="Migration from the previous billing system" description={config.migration.status === 'completed' ? `Completed ${formatDateTime(report?.ranAt)} by ${report?.ranByName}.` : `${b.legacyCounts.invoices} invoices, ${b.legacyCounts.payments} payments, ${b.legacyCounts.salesReceipts} sales receipts, ${b.legacyCounts.creditNotes} credit notes and ${b.legacyCounts.quotations} quotations are waiting to be copied. Original numbers are preserved and reserved.`} data-id="element-5283" data-mp="fp9rkt6h8490">
        {config.migration.status !== 'completed' && <Button onClick={migrate} isLoading={busy} disabled={!can('manageSettings')} data-id="element-5284" data-mp="uefmi4ya78b3">Run migration</Button>}
        {msg && <p role={msg.ok ? 'status' : 'alert'} className={`text-sm ${msg.ok ? 'text-emerald-700' : 'text-red-700'}`} data-id="element-5285" data-mp="18cmdd14tc819">{msg.text}</p>}
        {report && <div className="grid gap-4 md:grid-cols-2" data-id="element-5286" data-mp="3a5a9kqj1hv7j">
            <table className="text-sm" data-id="element-5287" data-mp="3a7wr9izn9r3h"><thead className="text-left text-[11px] uppercase text-slate-500" data-id="element-5288" data-mp="3578sgb56c5yp"><tr data-id="element-5289" data-mp="3593ddpgg3y2s"><th className="py-1 pr-3" data-id="element-5290" data-mp="35938pc5i39wg">Totals check</th><th className="py-1 pr-3 text-right" data-id="element-5291" data-mp="2kc46gj6cz5v8">Old system</th><th className="py-1 text-right" data-id="element-5292" data-mp="2k989oy68yn8d">Migrated</th></tr></thead>
              <tbody data-id="element-5293" data-mp="2oblb5adynyzc">{report.totals.map(t => <tr key={t.label} className={t.legacy !== t.migrated ? 'text-red-700' : ''} data-id="element-5294" data-mp="2oatjganhilec"><td className="py-1 pr-3" data-id="element-5295" data-mp="2t463e7h7y40r">{t.label}</td><td className="py-1 pr-3 text-right tabular-nums" data-id="element-5296" data-mp="2y2pq4a3mzju6">{t.legacy.toFixed(2)}</td><td className="py-1 text-right tabular-nums" data-id="element-5297" data-mp="2y4tt8ptpoete">{t.migrated.toFixed(2)}</td></tr>)}</tbody></table>
            <div className="text-xs" data-id="element-5298" data-mp="21et8d1wgqihd">{report.mismatches.length === 0 ? <p className="text-emerald-700" data-id="element-5299" data-mp="25f3fwizwlgyc">All totals reconcile.</p> : <ul className="list-disc space-y-1 pl-4 text-red-700" data-id="element-5300" data-mp="2ad4yz3xbxg9t">{report.mismatches.map(m => <li key={m} data-id="element-5301" data-mp="2afhnjzxleovu">{m}</li>)}</ul>}{report.skipped.length > 0 && <ul className="mt-2 list-disc space-y-1 pl-4 text-slate-600" data-id="element-5302" data-mp="2fd10mwik0vm7">{report.skipped.slice(0, 10).map(m => <li key={m} data-id="element-5303" data-mp="3lrienlhxiper">{m}</li>)}</ul>}</div>
          </div>}
      </SettingsPanel>

      <SettingsPanel title="Integration status" description="What is connected, and what must be set up outside the app." data-id="element-5304" data-mp="3qqbvnnwfcyjo">
        <ul className="divide-y divide-slate-100" data-id="element-5305" data-mp="1f39pq6oz33lu">
          {checks.map(c => <li key={c.label} className="flex gap-3 py-2.5" data-id="element-5306" data-mp="1a69ihsgdzut5">
              {c.ok ? <CheckCircle2Icon className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" aria-hidden="true" data-id="element-5307" data-mp="15dq3cr21wrm8" /> : <CircleAlertIcon className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" aria-hidden="true" data-id="element-5308" data-mp="10fon0dw0f6eg" />}
              <div data-id="element-5309" data-mp="we352e4mc1xe"><p className="text-sm font-semibold text-slate-900" data-id="element-5310" data-mp="wdtn9dtq912s">{c.label}</p><p className="text-xs text-slate-600" data-id="element-5311" data-mp="wb77iqwonf9h">{c.detail}</p></div>
            </li>)}
        </ul>
      </SettingsPanel>
    </div>;
}